import { TableCell, TableRow, Theme, withStyles } from '@material-ui/core';
import React, { memo } from 'react';
import { getValueByPropName } from '../../utils/helpers';
import { isDarkTheme } from '../../eliot-components/src/theme';
import { useLongPress } from '../../hooks/useLongPress';

export const StyledTableRow = withStyles((theme: Theme) => ({
  root: {
    '&.clickable': {
      cursor: 'pointer',
    },
    '&.pinned': {
      backgroundColor: isDarkTheme(theme)
        ? theme.palette.primary.dark
        : theme.palette.primary.light + ' !important',
    },
    '& td': {
      padding: theme.spacing(0, 1),
      '&.noPadding': {
        padding: '0px 0px',
      },
    },
    '& th': {
      padding: theme.spacing(0, 1),
      '&.noPadding': {
        padding: '0px 0px',
      },
    },
  },
}))(TableRow);

const TableCellWithLongPress = ({
  column,
  entity,
  getKeyValue,
  onDoubleClick,
}: {
  column: any;
  entity: any;
  getKeyValue: (entity: any) => string;
  onDoubleClick?: (txt: string) => void;
}) => {
  const isLongPressRef = React.useRef(false);
  const cellRef = React.useRef<HTMLTableCellElement>(null);

  const longPressHandlers = useLongPress({
    onLongPress: (e) => {
      isLongPressRef.current = true;
      e.preventDefault();
      e.stopPropagation();
      if (onDoubleClick && cellRef.current) {
        const cellText = !!column.cell
          ? cellRef.current.textContent || ''
          : String(getValueByPropName(entity, column.name) || '');
        onDoubleClick(cellText);
      }
    },
    delay: 500,
  });

  const handleClick = (e: React.MouseEvent) => {
    if (isLongPressRef.current) {
      e.preventDefault();
      e.stopPropagation();
      isLongPressRef.current = false;
    }
  };

  return (
    <TableCell
      ref={cellRef}
      className={column.noPadding ? 'noPadding' : ''}
      key={getKeyValue(entity) + column.name}
      onClick={handleClick}
      {...longPressHandlers}
    >
      {!!column.cell
        ? column.cell(entity)
        : getValueByPropName(entity, column.name)}
    </TableCell>
  );
};

export const InfiniteTableRow = memo(
  ({
    entity,
    tableColumns,
    getKeyValue,
    onRowClick,
    onDoubleClick,
  }: {
    entity: any;
    tableColumns: any[];
    getKeyValue: (entity: any) => string;
    onRowClick?: (entity: any) => void;
    onDoubleClick?: (txt: string) => void;
  }) => (
    <StyledTableRow
      className={`${onRowClick ? 'clickable' : ''} ${
        entity.isPinned ? 'pinned' : ''
      }`}
      key={getKeyValue(entity)}
      onClick={(e) => {
        if (
          onRowClick &&
          e.target &&
          //@ts-ignore
          e.target.tagName !== undefined &&
          //@ts-ignore
          ['td', 'i'].indexOf(e.target.tagName.toLowerCase()) > -1
        ) {
          const selection = window.getSelection();
          const selectedText = selection ? selection.toString() : '';
          e.preventDefault();
          if (selectedText && onDoubleClick) {
            onDoubleClick(selectedText);
          } else {
            onRowClick(entity);
          }
        }
      }}
    >
      {tableColumns.map((column) => (
        <TableCellWithLongPress
          key={getKeyValue(entity) + column.name}
          column={column}
          entity={entity}
          getKeyValue={getKeyValue}
          onDoubleClick={onDoubleClick}
        />
      ))}
    </StyledTableRow>
  ),
  (prevProps, nextProps) => {
    if (
      prevProps.entity === nextProps.entity &&
      prevProps.tableColumns === nextProps.tableColumns
    ) {
      return true; // Component will use cached value and not re-render
    }
    return false; // Component will re-render
  }
);
